import './previewActivationPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import {
  EMAIL_ADDRESS_PATTERN,
  PERSON_NAME_PATTERN,
  PHONE_NUMBER_PATTERN,
} from '@thesavingsgroup/regex';
import { toDigits } from '@thesavingsgroup/utils/toDigits';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import trim from 'lodash/trim';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import vehicleSUVSideView from '../../../assets/vehicle-suv-sideview-two.png';
import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { LanderImage } from '../../../components/LanderImage/LanderImage';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import useApiStore from '../../../store/apiStore/useApiStore';
import { DeliverPinLocation } from '../../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../../store/pinStore/usePinStore';
import usePreviewStore from '../../../store/previewStore/usePreviewStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useSignUpStore from '../../../store/signUpStore/useSignUpStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { isLoading } = useApiStore();

  const { t } = useTranslation();

  const { deliverPin, deliverPinFailedMessage } = usePinStore();

  const { deriveDeliverPinParams, clearUserSuccessState, userExists } =
    useUserStore();

  const {
    firstName,
    lastName,
    primaryPhoneNumber,
    primaryEmailAddress,
    setPreviewActivationState,
  } = usePreviewStore();

  const form = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      firstNameValue: firstName,
      lastNameValue: lastName,
      primaryEmailAddressValue: primaryEmailAddress,
      primaryPhoneNumberValue: toPhoneString(primaryPhoneNumber),
    },
    delayError: 200,
    shouldFocusError: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const firstNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: PERSON_NAME_PATTERN,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: PERSON_NAME_PATTERN,
      message: t('validation.lastName.invalid'),
    },
  };

  const emailAddressValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.emailAddress.required'),
    pattern: {
      value: EMAIL_ADDRESS_PATTERN,
      message: t('validation.emailAddress.invalid'),
    },
  };

  const phoneNumberValidation = {
    setValueAs: (value: string): string => toDigits(value),
    required: t('validation.phone.required'),
    pattern: {
      value: PHONE_NUMBER_PATTERN,
      message: t('validation.phone.invalid'),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      firstNameValue,
      lastNameValue,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
    } = values;

    await userExists(primaryPhoneNumberValue, primaryEmailAddressValue);

    const { userExistsException } = useUserStore.getState();

    setPreviewActivationState({
      firstNameValue,
      lastNameValue,
      primaryEmailAddressValue,
      primaryPhoneNumberValue,
    });

    const deliverPinParams = deriveDeliverPinParams(
      userExistsException,
      DeliverPinLocation.SIGN_IN,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
    );

    await deliverPin(deliverPinParams);

    const {
      computed: { isAccountOwnershipUnverified },
      deliverPinSuccess,
    } = usePinStore.getState();

    if (deliverPinSuccess) {
      navigateWithCallback('/preview/pin-verify').then((): void => {
        clearUserSuccessState();
      });
    }

    // CP-6949: the email belongs to an existing account and the phone is not
    // on it. Verifying a new phone does not prove the account is theirs, so
    // explain the options instead of sending a PIN
    if (isAccountOwnershipUnverified) {
      // the returning-user page reads what was entered from the sign-up store
      useSignUpStore.setState({
        primaryEmailAddress: primaryEmailAddressValue,
        primaryPhoneNumber: primaryPhoneNumberValue,
      });

      // plain navigate: navigateWithCallback only navigates inside .then()
      navigate('/sign-up/returning-user');
    }
  };

  return (
    <div className="preview-activation-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header showLogo />

          <main>
            <LanderImage
              alt={t('imageAlts.vehicleSUVSideView')}
              defaultImage={vehicleSUVSideView}
            />
            <form
              data-testid={locator.pages.previewActivation.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.previewActivation.form.h1}>
                  {t('PreviewActivationPage.title')}
                </h1>
                <h2 data-testid={locator.pages.previewActivation.form.h2}>
                  {t('PreviewActivationPage.subtitle')}
                </h2>

                {deliverPinFailedMessage && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={locator.pages.previewActivation.alert.error}
                      severity="error"
                    >
                      {deliverPinFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Input
                        {...register('firstNameValue', firstNameValidation)}
                        data-testid={
                          locator.pages.previewActivation.form.firstNameInput
                        }
                        error={errors.firstNameValue}
                        errorTestId={
                          locator.pages.previewActivation.form
                            .firstNameInputError
                        }
                        label={t('PreviewActivationPage.firstName')}
                      />
                    </div>

                    <div className="input-half">
                      <Input
                        {...register('lastNameValue', lastNameValidation)}
                        data-testid={
                          locator.pages.previewActivation.form.lastNameInput
                        }
                        error={errors.lastNameValue}
                        errorTestId={
                          locator.pages.previewActivation.form
                            .lastNameInputError
                        }
                        label={t('PreviewActivationPage.lastName')}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <NumberInput
                      {...register(
                        'primaryPhoneNumberValue',
                        phoneNumberValidation,
                      )}
                      autoComplete="tel-national"
                      data-testid={
                        locator.pages.previewActivation.form.phoneInput
                      }
                      error={errors.primaryPhoneNumberValue}
                      errorTestId={
                        locator.pages.previewActivation.form.phoneInputError
                      }
                      label={t('PreviewActivationPage.phone')}
                      phone
                    />
                  </div>
                  <div>
                    <Input
                      {...register(
                        'primaryEmailAddressValue',
                        emailAddressValidation,
                      )}
                      data-testid={
                        locator.pages.previewActivation.form.emailInput
                      }
                      error={errors.primaryEmailAddressValue}
                      errorTestId={
                        locator.pages.previewActivation.form.emailInputError
                      }
                      label={t('PreviewActivationPage.email')}
                    />
                  </div>
                </div>

                <Button
                  data-testid={
                    locator.pages.previewActivation.form.submitButton
                  }
                  loader={isLoading}
                  loaderPosition="start"
                  fullWidth
                  submit
                >
                  {t('PreviewActivationPage.continue')}
                </Button>
                <p className="form-terms-privacy">
                  <Trans
                    components={{
                      terms: (
                        <a
                          data-testid={
                            locator.pages.previewActivation.form.termsLink
                          }
                          href="https://www.thesavingsgroup.com/terms-of-use/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                      privacy: (
                        <a
                          data-testid={
                            locator.pages.previewActivation.form.privacyLink
                          }
                          href="https://www.thesavingsgroup.com/privacy-policy/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                    }}
                    i18nKey={'PreviewActivationPage.termsPrivacy'}
                  />
                </p>
              </div>
              <footer data-testid={locator.pages.previewActivation.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'PreviewActivationPage';
